import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from './Modal';
import api from '../services/api';

function VideoSessionAssignModal({ session, onClose }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);

  const searchBoxRef = useRef(null);
  const dropdownRef = useRef(null);

  const fetchShares = async () => {
    setLoading(true);
    try {
      const response = await api.getVideoSessionShares(session.id, 1, 100);
      setShares(response.items || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.getUsers(1, 10, query.trim());
        setSearchResults(response.items || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setSearching(false);
      }
    }, query.trim() ? 300 : 0);

    return () => clearTimeout(timer);
  }, [query, isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedSearchBox = searchBoxRef.current && searchBoxRef.current.contains(e.target);
      const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!clickedSearchBox && !clickedDropdown) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = () => {
    if (searchBoxRef.current) {
      const rect = searchBoxRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setIsDropdownOpen(true);
  };

  const isAlreadyShared = (userId) =>
    shares.some((share) => share.shared_with_user_id === userId);

  const handleAssign = async (user) => {
    if (isAlreadyShared(user.id)) return;

    setError(null);
    try {
      await api.createVideoSessionShare(session.id, user.id);
      setQuery('');
      fetchShares();
    } catch (err) {
      if (err.status === 409) {
        setError('User này đã được assign session này rồi.');
      } else {
        setError(err.message);
      }
    }
  };

  const handleUnassign = async (share) => {
    if (!window.confirm(`Bỏ assign "${share.shared_with_username}" khỏi session này?`)) {
      return;
    }
    setError(null);
    try {
      await api.deleteVideoSessionShare(session.id, share.id);
      fetchShares();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Modal title={`Assign session #${session.id}`} onClose={onClose}>
      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label>Đang được chia sẻ với</label>
        {loading ? (
          <p>Loading...</p>
        ) : shares.length === 0 ? (
          <p>Chưa assign cho ai.</p>
        ) : (
          <ul className="share-list">
            {shares.map((share) => (
              <li key={share.id} className="share-item">
                <span>{share.shared_with_username}</span>
                <button
                  type="button"
                  className="share-item-remove"
                  aria-label="Unassign"
                  onClick={() => handleUnassign(share)}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="form-group">
        <label>Assign cho user khác</label>
        <div className="assign-search" ref={searchBoxRef}>
          <input
            type="text"
            placeholder="Tìm theo username/email..."
            value={query}
            onFocus={openDropdown}
            onChange={(e) => {
              setQuery(e.target.value);
              openDropdown();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setIsDropdownOpen(false);
            }}
          />
          <span className={`assign-search-caret${isDropdownOpen ? ' is-open' : ''}`}>
            &#9662;
          </span>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Đóng
        </button>
      </div>

      {isDropdownOpen &&
        dropdownRect &&
        createPortal(
          <ul
            ref={dropdownRef}
            className="assign-dropdown"
            style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
          >
            {searching ? (
              <li className="assign-dropdown-empty">Đang tìm...</li>
            ) : searchResults.length === 0 ? (
              <li className="assign-dropdown-empty">Không tìm thấy user</li>
            ) : (
              searchResults.map((user) => {
                const alreadyShared = isAlreadyShared(user.id);
                return (
                  <li
                    key={user.id}
                    className={`assign-dropdown-item${alreadyShared ? ' is-disabled' : ''}`}
                    onClick={() => handleAssign(user)}
                  >
                    <span>
                      {user.username} {user.full_name ? `(${user.full_name})` : ''}
                    </span>
                    {alreadyShared && <span className="assign-dropdown-tag">Đã assign</span>}
                  </li>
                );
              })
            )}
          </ul>,
          document.body
        )}
    </Modal>
  );
}

export default VideoSessionAssignModal;
